import { Script } from "gatsby";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import classNames from "classnames";
import { trackCustomEvent } from "../utilities/analytics";

const IFRAME_API = "https://www.youtube.com/iframe_api";

type Props = {
  id: string;
  title: string;
  height?: number | string;
  width?: number | string;
  className?: string;
  turnOnCaptions?: boolean;
};

const YouTubeEmbed: React.FC<Props> = ({
  className,
  id,
  title,
  width,
  height,
  turnOnCaptions = false,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const videoRef = useRef<HTMLIFrameElement>(null!);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const src = useMemo(
    () =>
      `https://www.youtube-nocookie.com/embed/${id}?enablejsapi=1&modestbranding=1${
        turnOnCaptions ? "&cc_load_policy=1&cc_lang_pref=en&hl=en" : ""
      }`,
    [id]
  );

  const onScriptLoad = useCallback(() => {
    setScriptLoaded(true);
  }, []);

  useEffect(() => {
    if (scriptLoaded) {
      window.onYouTubeIframeAPIReady = () => {
        new window.YT.Player(videoRef.current, {
          events: {
            /* istanbul ignore next */
            onStateChange({ data }) {
              const gaEvent = {
                [YT.PlayerState.UNSTARTED]: null,
                [YT.PlayerState.ENDED]: { action: "Video Completes" },
                [YT.PlayerState.PLAYING]: { action: "Video Plays" },
                [YT.PlayerState.PAUSED]: { action: "Video Paused" },
                [YT.PlayerState.BUFFERING]: null,
                [YT.PlayerState.CUED]: null,
              }[data];

              trackCustomEvent({
                category: "Engagement",
                action: "Video Interaction",
                label: id,
                ...gaEvent,
              });
            },
          },
        });
      };

      return () => {
        window.onYouTubeIframeAPIReady = null;
      };
    }
  }, [id, scriptLoaded]);

  return (
    <>
      <iframe
        data-testid="youtube-embed"
        width={width}
        height={height}
        ref={videoRef}
        title={title}
        src={src}
        frameBorder="0"
        allowFullScreen
        className={classNames("responsive__item cqYT", className)}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
      />
      <Script
        data-testid="youtube-embed-script"
        src={IFRAME_API}
        onLoad={onScriptLoad}
      />
    </>
  );
};

export default YouTubeEmbed;
